// src/index.ts
import { writable } from "sveltore";
import { logs } from "named-logs";
import { throttle } from "lodash-es";
import { initEmitter } from "radiate";
var logger = logs("tx-observer");
function initTransactionProcessor(config) {
  const emitter = initEmitter();
  let provider;
  const $txs = [];
  const store = writable($txs);
  const map = {};
  function add(list) {
    logger.info(`adding ${list.length} txs...`);
    for (const tx of list) {
      _addSingle(tx);
    }
    store.set($txs);
  }
  function _addSingle(pendingTransaction) {
    logger.info(`adding ${pendingTransaction.hash}...`);
    if (!map[pendingTransaction.hash]) {
      map[pendingTransaction.hash] = pendingTransaction;
      $txs.push(pendingTransaction);
    }
  }
  function addTx(tx, hash, state) {
    if (!map[hash]) {
      const pendingTransaction = {
        hash,
        request: tx,
        ...state || {
          inclusion: "BeingFetched",
          final: void 0,
          status: void 0
        }
      };
      _addSingle(pendingTransaction);
      store.set($txs);
    }
  }
  function clear() {
    logger.info(`clearing txs...`);
    for (const tx of $txs) {
      delete map[tx.hash];
    }
    $txs.splice(0, $txs.length);
  }
  function remove(hash) {
    logger.info(`removing tx ${hash}...`);
    const pendingTransaction = map[hash];
    if (pendingTransaction) {
      const index = $txs.indexOf(pendingTransaction);
      if (index >= 0) {
        $txs.splice(index, 1);
        store.set($txs);
      }
      delete map[hash];
    }
  }
  async function process() {
    if (!provider) {
      return;
    }
    const latestBlock = await provider.request({
      method: "eth_getBlockByNumber",
      params: ["latest", false]
    });
    if (!latestBlock) {
      return;
    }
    const latestBlockTime = Number(latestBlock.timestamp);
    const latestBlockNumber = Number(latestBlock.number);
    logger.info(`latestBlock: ${latestBlockNumber}`);
    const latestFinalizedBlockNumber = Math.max(latestBlockNumber - config.finality, 0);
    const latestFinalizedBlock = await provider.request({
      method: "eth_getBlockByNumber",
      params: [`0x${latestFinalizedBlockNumber.toString(16)}`, false]
    });
    if (!latestFinalizedBlock) {
      return;
    }
    const latestFinalizedBlockTime = Number(latestFinalizedBlock.timestamp);
    logger.info(`latestFinalizedBlock: ${latestFinalizedBlockNumber}`);
    logger.info(`txs: ${$txs.length}`);
    let changes = false;
    for (const tx of $txs) {
      const newChanges = await processTx(tx, {
        latestBlockNumber,
        latestBlockTime,
        latestFinalizedBlock,
        latestFinalizedBlockTime
      });
      changes = changes || newChanges;
    }
    if (changes) {
      store.set($txs);
    }
  }
  async function processTx(tx, {
    latestBlockNumber,
    latestBlockTime,
    latestFinalizedBlock,
    latestFinalizedBlockTime
  }) {
    if (!provider) {
      return false;
    }
    if (tx.inclusion === "Included") {
      if (tx.final) {
        return false;
      }
    }
    const txFromPeers = await provider.request({
      method: "eth_getTransactionByHash",
      params: [tx.hash]
    });
    let changes = false;
    if (txFromPeers) {
      let receipt;
      if (txFromPeers.blockNumber) {
        receipt = await provider.request({
          method: "eth_getTransactionReceipt",
          params: [tx.hash]
        });
      }
      if (receipt) {
        const block = await provider.request({
          method: "eth_getBlockByHash",
          params: [txFromPeers.blockHash, false]
        });
        if (block) {
          if (tx.inclusion !== "Included") {
            tx.inclusion = "Included";
            changes = true;
          }
          const blockNumber = Number(block.number);
          const blockTimestamp = Number(block.timestamp);
          const is_final = latestBlockNumber - blockNumber >= config.finality;
          if (receipt.status === "0x0" || receipt.status === "0x00") {
            if (tx.status !== "Failure" || tx.final !== blockTimestamp) {
              tx.status = "Failure";
              tx.final = is_final ? blockTimestamp : void 0;
              changes = true;
            }
          } else {
            if (tx.status !== "Success" || tx.final !== blockTimestamp) {
              tx.status = "Success";
              tx.final = is_final ? blockTimestamp : void 0;
              changes = true;
            }
          }
        }
      } else {
        if (tx.inclusion !== "Broadcasted") {
          tx.inclusion = "Broadcasted";
          tx.final = void 0;
          tx.status = void 0;
          tx.request.nonce = txFromPeers.nonce;
          changes = true;
        }
      }
    } else {
      const txFromPeers2 = await provider.request({
        method: "eth_getTransactionByHash",
        params: [tx.hash]
      });
      if (txFromPeers2) {
        return false;
      }
      const account = tx.request.from;
      const tranactionCount = await provider.request({
        method: "eth_getTransactionCount",
        params: [account, latestFinalizedBlock.hash]
      });
      const finalityNonce = Number(tranactionCount);
      logger.info(`finalityNonce: ${finalityNonce}`);
      if (typeof tx.request.nonce === "number" && finalityNonce > tx.request.nonce) {
        if (tx.inclusion !== "Cancelled" || !tx.final) {
          tx.inclusion = "Cancelled";
          tx.final = typeof tx.request.timestamp !== void 0 ? tx.request.timestamp : latestFinalizedBlockTime;
          tx.status = void 0;
          changes = true;
        }
      } else {
        if (tx.inclusion !== "NotFound") {
          tx.inclusion = "NotFound";
          tx.final = void 0;
          tx.status = void 0;
          changes = true;
        }
      }
    }
    if (changes) {
      if (map[tx.hash]) {
        emitter.emit(tx);
      }
    }
    return changes;
  }
  return {
    txs: {
      subscribe: store.subscribe
    },
    setProvider(newProvider) {
      provider = newProvider;
    },
    // we probably do not want to hook it up directly to web3-connection observers
    // most likely we want an account data store to be the observer and store the tx as they come, to ensure they are saved as soon as possible
    // then the account store can notify the tx observer to start processing
    // similarly on account loaded or switched, we will want to delete the tx
    // also we need to consider the provider changing ?
    // onTxSent(tx: EIP1193TransactionWithMetadata, hash: string) {
    // 	addTx(tx, hash, 'Broadcasted');
    // },
    add,
    remove,
    clear,
    process: throttle(process, 1e3),
    // TODO why onNewBlock is called so many time
    onTx: emitter.on,
    offTx: emitter.off
  };
}
export {
  initTransactionProcessor
};
//# sourceMappingURL=index.js.map